// Wire protocol for the loom-server ↔ coding-bridge WebSocket.
//
// The bridge dials `/internal/bridge/:sessionId/connect`; both sides
// exchange JSON frames over the long-lived socket. Defining the shapes
// here keeps `apps/server/src/ws/bridge.ts` (server-side parser) and
// `packages/coding-bridge/src/relay-client.ts` (bridge-side client) from
// drifting — if you add a variant, add it once here and both sides see
// it.
//
// `OutboundFrame` is *from the bridge's perspective* (bridge → server).
// `InboundFrame` is the opposite (server → bridge). The server-side
// parser at `apps/server/src/ws/bridge.ts` validates `OutboundFrame`
// shapes at runtime with Zod; the inferred type must stay structurally
// compatible with the canonical shape here.

import type { Forge } from "./forge/index.js";
import type { ModelUsageMap } from "./spend/index.js";

/**
 * Bridge lifecycle states surfaced via `status` frames. Mirrored on the
 * server side as `AgentSession["bridgeState"]` (plus the server-only
 * pre-attach `"pending"` state, which the bridge never emits).
 */
export type BridgeStatusState = "cloning" | "ready" | "terminated" | "error";

/**
 * Single coding-mode activity entry the bridge streams to the server.
 * The server pushes it onto `session.codingActivity` for the next
 * PM-mode summary and fans it out as a `coding_progress` event for the
 * UI timeline.
 */
export type CodingActivityEntry =
  | { readonly kind: "user_prompt"; readonly text: string }
  | {
      readonly kind: "file_touched";
      readonly path: string;
      readonly op: "read" | "edit" | "write";
    }
  | {
      readonly kind: "commit";
      readonly sha: string;
      readonly message: string;
    }
  | { readonly kind: "bash"; readonly command: string }
  | { readonly kind: "assistant_text"; readonly text: string };

/**
 * Budget counter snapshot the bridge persists each flush. The first three
 * fields are the v1 wire shape; everything optional after them is the Phase-9
 * per-model spend payload (cache counters, the `claude` CLI's own reported
 * USD, and the per-model `modelUsage` breakdown). A v1 bridge omits them; a
 * v1 server strips them — both directions stay compatible during a rolling
 * deploy.
 */
export interface BridgeBudgetSnapshot {
  readonly inputTokens: number;
  readonly outputTokens: number;
  /** Effective USD — the CLI-reported figure moved onto the rate Anthropic
   *  bills when available, table-priced otherwise. */
  readonly costUsd: number;
  readonly cacheCreationTokens?: number;
  readonly cacheReadTokens?: number;
  readonly cacheCreation5mTokens?: number;
  readonly cacheCreation1hTokens?: number;
  /** Cumulative CLI-reported USD (`total_cost_usd` summed across turns),
   *  verbatim — the input `costUsd` is derived from, never itself corrected. */
  readonly reportedCostUsd?: number;
  /** Accumulated per-model breakdown (the CLI's `modelUsage`). */
  readonly models?: ModelUsageMap;
  /** `costUsd` is on the billed rate rather than the CLI's own catalog. The
   *  server's monotonic budget mirror reads this to admit the one-time
   *  downward restatement of a session that straddles the correction. Absent
   *  on snapshots from a bridge that predates it. */
  readonly corrected?: boolean;
  /** The session's model rounds: every `result` frame's `num_turns`, summed,
   *  carried across a sandbox restart by the seed. The server reads it for
   *  the `implement.run_rounds` line. Absent from an older bridge. */
  readonly numTurns?: number;
}

/**
 * Coding-mode state envelope the bridge flushes via `persist` frames.
 */
export interface BridgePersistedState {
  readonly claudeSessionId: string | null;
  /** Base64-encoded JSONL bytes of the Claude CLI transcript. */
  readonly sessionJsonl: string;
  readonly budgetState: BridgeBudgetSnapshot;
  readonly branchName: string;
  readonly currentMode: "pm" | "coding";
  /**
   * Flow-boundary generation (Phase 1/2) this snapshot was captured at — the
   * session's `boundaryEpoch` when the bridge flushed. On resume the server
   * drops the snapshot's `claudeSessionId` when this is below the session's
   * current epoch (a boundary opened a fresh flow after the snapshot, so the
   * persisted CLI conversation belongs to the OLD flow). Optional — absent on
   * snapshots written by an older bridge → treated as epoch 0 (never fences,
   * the pre-Phase-2 behaviour).
   */
  readonly epoch?: number;
}

/**
 * Outbound shapes the bridge sends to loom-server. Mirrored by the
 * `BridgeFrameSchema` Zod union at `apps/server/src/ws/bridge.ts` — if
 * you add a variant here, add the schema there.
 */
export type OutboundFrame =
  | {
      readonly type: "status";
      readonly state: BridgeStatusState;
      readonly etaSeconds?: number;
      readonly message?: string;
    }
  | {
      readonly type: "request_proxy_token";
      /**
       * The forge the bridge was booted for (`LOOM_TARGET_FORGE`). A GitHub
       * bridge always sends `"github"`; a GitLab bridge omits it, so GitLab
       * frames stay byte-identical. The server vends only when this matches
       * the forge of the session's project id — an older bridge (no field)
       * on a GitHub session gets `set_proxy_token_error`, never a token.
       */
      readonly forge?: Forge;
    }
  | {
      readonly type: "rpc_response";
      readonly correlationId: string;
      readonly ok: boolean;
      // Zod's `z.unknown()` parses to `unknown | undefined`, so the
      // server-side schema treats `payload` as optional. The sender
      // always provides it; the type is permissive to match the
      // parsed-frame shape downstream code consumes.
      readonly payload?: unknown;
    }
  | {
      readonly type: "stream";
      readonly channel: "stdout" | "stderr";
      readonly chunk: string;
    }
  | {
      readonly type: "stream";
      readonly payload?: unknown;
    }
  | {
      readonly type: "claude_session_id";
      readonly id: string;
      /**
       * Flow-boundary generation (Phase 1/2) the spawn that captured this id was
       * running at. A dying prior spawn's tail flush carries its OLD epoch, so
       * the server fences the update when `epoch < session.boundaryEpoch`
       * (a boundary already moved the session to a fresh flow). Optional — an
       * older bridge omits it → treated as epoch 0, never fenced.
       */
      readonly epoch?: number;
    }
  | {
      readonly type: "branch_state";
      readonly commits: number;
      readonly summary: string | null;
    }
  | {
      readonly type: "coding_activity_entry";
      readonly entry: CodingActivityEntry;
    }
  | ({ readonly type: "persist" } & BridgePersistedState)
  | { readonly type: "request_resume_state" }
  | {
      readonly type: "diag";
      readonly event: string;
      readonly fields?: Record<string, unknown>;
    }
  | {
      readonly type: "turn_state";
      /**
       * ABSOLUTE census of the turns this bridge owns right now: the live
       * `claude` process (0 or 1) plus everything queued behind it.
       *
       * The server's own accounting is a DELTA counter — `+1` on every
       * `user_prompt` it forwards, `−1` only on a relayed `loom_stream_end`.
       * Every way a turn can end WITHOUT that frame (a prompt refused at the
       * budget wall, a `stream` frame dropped because the relay socket was
       * mid-reconnect, a queued prompt that never spawned) leaves the counter
       * wedged above zero with a live bridge — and a wedged counter is a
       * session pinned on "busy" forever, with the split's Confirm button
       * disabled behind it. A census SETS; it cannot accumulate error.
       */
      readonly inFlight: number;
      /**
       * The highest `user_prompt.promptSeq` this bridge has seen. The server
       * ignores a census below its own `bridgePromptSeq` — a census emitted
       * before the newest prompt landed is stale, and adopting it would flip a
       * genuinely-busy session to idle (which releases the client's batched
       * answers into a live turn).
       */
      readonly ackPromptSeq: number;
    };

/**
 * Inbound shapes loom-server sends to the bridge. The bridge tolerates
 * unknown `type` values — they log a warning but do not crash the loop —
 * which the trailing `Record<string, unknown>` member captures at the
 * type level.
 */
export type InboundFrame =
  | {
      readonly type: "set_proxy_token";
      readonly token: string;
      readonly expiresAt: string;
      /**
       * Set only on a GitHub token. A GitHub-mode bridge rejects a
       * `set_proxy_token` without it, so an older server that ignores the
       * request's `forge` and vends a GitLab token can never have that token
       * sent to github.com. GitLab replies never carry it.
       */
      readonly forge?: "github";
    }
  /**
   * `message` is a machine word. Besides the vendor's own failures it can be
   * `forge_mismatch` (the request's forge is not the session's) or
   * `github_disabled` (a GitHub session on a server without GitHub wiring).
   */
  | { readonly type: "set_proxy_token_error"; readonly message: string }
  | { readonly type: "gitlab_token_expired" }
  | { readonly type: "shutdown"; readonly reason?: string }
  /**
   * Rotate the bridge's own attach credential over the already-authenticated
   * socket.
   *
   * `LOOM_BRIDGE_JWT` is minted once at pipeline-trigger time with a 1h TTL and
   * arrives as a CI variable, so it was immutable for the job's whole life. Any
   * reconnect after that hour presented an expired token, got `4401
   * jwt_expired`, and — because the reconnect loop treats an auth close like a
   * transient drop — retried at the 30s backoff cap until the job died. That is
   * the mechanism behind 1,432 rejected attaches over 60 days (8 sessions,
   * worst 506 attempts across ~4h): a GitLab job `retry` re-runs with the SAME
   * expired trigger variable, so attempts 2 and 3 could never authenticate at
   * all and storm for their entire 60-minute life.
   *
   * The server pushes a freshly-minted token at ~50% of TTL (and immediately on
   * attach if the presented one is already past that point). The bridge keeps
   * the newest one in memory and dials with it, so the hourly socket death that
   * `catladder.ts`'s 60m Cloud Run request timeout guarantees becomes an
   * ordinary reconnect instead of a terminal one.
   *
   * Safe over this channel: the socket is already authenticated by the token
   * being replaced, so this grants no capability the peer does not already hold.
   * An older bridge ignores the unknown frame type and keeps its boot token —
   * i.e. it degrades to exactly today's behaviour, never worse.
   */
  | {
      readonly type: "set_bridge_token";
      readonly token: string;
      /** ISO-8601 expiry of `token`. Diagnostics only — the bridge does not
       *  gate on it (the server owns rotation cadence). */
      readonly expiresAt: string;
    }
  /**
   * Where the sandbox's `claude` sends its Anthropic calls, and the token it
   * sends with them: the server's model gateway
   * (`apps/server/src/model-gateway/`), which puts the key on each call. Sent
   * on an attach while `LOOM_MODEL_GATEWAY` is on, the session's job started
   * on the gateway and the attach's bridge JWT names that job (`modelGen`);
   * an earlier job of the session gets none. `token` is a fresh `m1` for the
   * session's current job, never an Anthropic key, and is never logged. It
   * is no pipeline variable itself, but whoever holds the job's
   * `LOOM_BRIDGE_JWT` (one) can attach and receive it. A bridge that does
   * not know the frame ignores it. Re-sent with a fresh token at 2/3 of
   * each token's life while the socket stays attached and names the
   * session's current job (`model-gateway/credential.ts`); the bridge keeps
   * the newest and drops one already past `expiresAt`.
   */
  | {
      readonly type: "set_model_credential";
      /** The gateway's root, for `ANTHROPIC_BASE_URL`. */
      readonly baseUrl: string;
      readonly token: string;
      /** ISO-8601 expiry of `token`. */
      readonly expiresAt: string;
    }
  /**
   * Raise this session's spend ceiling, in place, without losing the session.
   *
   * The budget wall is a cost guard, not a verdict on the work: a session that
   * hits it has a fully-grounded card and a live sandbox, and its only exits
   * were "apply what you have" or "throw it away and start again at the same
   * limit". Both were observed stranding a refine mid-split. This lets the user
   * keep going from where they are. Raise-only — the bridge ignores a value at
   * or below the current ceiling, so this frame can never tighten a limit or be
   * replayed to shrink one.
   */
  | { readonly type: "raise_budget"; readonly limitUsd: number }
  | {
      readonly type: "rpc_request";
      readonly correlationId: string;
      readonly method: string;
      readonly params: unknown;
    }
  | {
      readonly type: "user_prompt";
      readonly text: string;
      readonly claudeSessionId?: string;
      /**
       * Flow-boundary generation (Phase 1/2) to stamp on the spawn this prompt
       * starts, so a late frame from a superseded spawn can be fenced by epoch.
       * Optional — an older bridge ignores it.
       */
      readonly epoch?: number;
      /**
       * Force a fresh CLI conversation for this prompt (skip `--resume`) — a
       * boundary turn. The bridge resets its in-memory + persisted resume id so
       * the next spawn does not continue the prior flow's claude session.
       * Optional — an older bridge ignores it (the server's `claudeSessionId`
       * clear on a boundary already keeps the prompt resume-free).
       */
      readonly newChat?: boolean;
      /**
       * Monotonic per-session prompt counter the server stamps on every
       * forwarded prompt. The bridge echoes the highest one it has seen on its
       * `turn_state` census, so the server can tell a census that already
       * accounts for its newest prompt (adopt it) from one that predates it
       * (ignore it). Optional — an older bridge omits the echo, and a census
       * that never arrives leaves today's delta accounting untouched.
       */
      readonly promptSeq?: number;
    }
  | {
      readonly type: "set_resume_state";
      readonly state: BridgePersistedState | null;
    }
  | Record<string, unknown>;

/** The bridge's budget-exhaustion stream error tag. Single-sourced here so the
 *  bridge emitter and the server translator match on one literal. */
export const BUDGET_EXHAUSTED_ERROR = "budget_exhausted";

/**
 * Detect the bridge's budget-exhaustion stream payload
 * (`{type:"loom_error", error:"budget_exhausted", message}`) and pull the
 * "$used / $limit" figures from the human message. Returns null for any other
 * payload; the numbers are best-effort (0 on message drift) — the FACT of
 * exhaustion is what callers gate on. Plain capture groups (no lookbehind).
 */
/** The bridge's "cannot reach Loom's tools" stream error tag. Single-sourced
 *  here so the bridge emitter and the server translator match on one literal. */
export const MCP_DISCONNECTED_ERROR = "mcp_disconnected";

/**
 * Detect the bridge's tool-unavailability stream payload
 * (`{type:"loom_error", error:"mcp_disconnected", message}`).
 *
 * Classifies auth REFUSAL (the MCP server answered with a 401) apart from
 * plain unreachability, because the operator response differs: a refusal means
 * the session is revoked or its bearer expired, unreachability means the
 * server is down or the URL is wrong. Returns null for any other payload.
 */
export const parseMcpDisconnectedStream = (
  payload: unknown,
): { readonly reason: "refused" | "unreachable"; readonly detail: string } | null => {
  if (payload === null || typeof payload !== "object") return null;
  const p = payload as { type?: unknown; error?: unknown; message?: unknown };
  if (p.type !== "loom_error" || p.error !== MCP_DISCONNECTED_ERROR) return null;
  const detail = typeof p.message === "string" ? p.message : "";
  const refused = /unauthorized|401|forbidden|403/i.test(detail);
  return { reason: refused ? "refused" : "unreachable", detail: detail.slice(0, 300) };
};

/** The bridge's "the model refused this sandbox's credential" stream error
 *  tag: `{type:"loom_error", error:"model_access_failed", reason, message}`.
 *  `reason` is `auth` (the CLI's `authentication_failed`: on the gateway an
 *  invalid or expired `m1`), `billing` (its `billing_error`: the key behind
 *  the call has no credit), `expired` (the bridge refused to spawn on a
 *  gateway credential past its `expiresAt`) or `missing` (a gateway job, by
 *  its bridge JWT's `modelGen`, with no credential yet: the bridge refused
 *  to spawn on the job's own key). Single-sourced here so the bridge emitter
 *  and the server translator match on one literal. */
export const MODEL_ACCESS_FAILED_ERROR = "model_access_failed";

export type ModelAccessFailure = "auth" | "billing" | "expired" | "missing";

export const parseModelAccessFailedStream = (
  payload: unknown,
): { readonly reason: ModelAccessFailure } | null => {
  if (payload === null || typeof payload !== "object") return null;
  const p = payload as { type?: unknown; error?: unknown; reason?: unknown };
  if (p.type !== "loom_error" || p.error !== MODEL_ACCESS_FAILED_ERROR) return null;
  if (
    p.reason !== "auth" &&
    p.reason !== "billing" &&
    p.reason !== "expired" &&
    p.reason !== "missing"
  ) {
    return null;
  }
  return { reason: p.reason };
};

export const parseBudgetExhaustedStream = (
  payload: unknown,
): { readonly usedUsd: number; readonly limitUsd: number } | null => {
  if (payload === null || typeof payload !== "object") return null;
  const p = payload as { type?: unknown; error?: unknown; message?: unknown };
  if (p.type !== "loom_error" || p.error !== BUDGET_EXHAUSTED_ERROR) return null;
  const msg = typeof p.message === "string" ? p.message : "";
  const m = /\$([0-9]+(?:\.[0-9]+)?)\s*\/\s*\$([0-9]+(?:\.[0-9]+)?)/.exec(msg);
  return { usedUsd: m ? Number(m[1]) : 0, limitUsd: m ? Number(m[2]) : 0 };
};

// ---------------------------------------------------------------------------
// `diag` frame — content-free bridge diagnostics.
//
// The sandbox's only log surface is the GitLab CI job trace, which needs the
// job id to read. That makes a tool-less turn (MCP probe failed → zero loom
// tool calls → no `propose_card`) indistinguishable from a healthy one in
// Cloud Logging. The `diag` frame carries the counts/statuses that answer that
// question server-side; the server logs them, nothing is emitted on the bus.
//
// This is a TRUST BOUNDARY: the bridge hosts model-authored code, so the
// server never spreads bridge-supplied keys into a log line. `sanitizeDiagFields`
// is the single gate — allowlisted keys, scalar values only, bounded count and
// bounded string length.
// ---------------------------------------------------------------------------

/** Field names a `diag` frame may carry. Anything else is dropped. */
export const DIAG_ALLOWED_FIELDS: ReadonlySet<string> = new Set([
  // Where to look next: the GitLab job whose trace holds the full picture.
  "jobId",
  "pipelineId",
  "intent",
  "epoch",
  // mcp_probe_failed
  "errorClass",
  "status",
  "timedOut",
  "attempt",
  "elapsedMs",
  "initStatus",
  // turn_end
  "proposeCardEmitted",
  "loomToolCalls",
  "toolCalls",
  "durationMs",
  "exitCode",
  "signal",
  "subtype",
  "sawText",
  // The spawn's model rounds (its results' `num_turns`): logs only.
  "numTurns",
]);

/**
 * Hard ceiling on how many fields survive sanitisation, whatever the frame says.
 *
 * Derived from the allowlist rather than fixed, because a fixed number smaller
 * than the allowlist is a landmine: adding two more `turn_end` fields would
 * silently drop fields in insertion order, with no failing test and no signal
 * in the log that anything went missing. The cap's job is to bound an
 * ADVERSARIAL frame — a bridge sending a thousand keys — and the allowlist
 * already bounds the legitimate case exactly.
 */
export const DIAG_MAX_FIELDS = DIAG_ALLOWED_FIELDS.size;

/** Hard ceiling on a single string value's length. */
export const DIAG_MAX_VALUE_CHARS = 200;

/** `diag` event names are log message discriminators, not free text. */
const DIAG_EVENT_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export const isValidDiagEvent = (event: unknown): event is string =>
  typeof event === "string" && DIAG_EVENT_PATTERN.test(event);

/**
 * Reduce a bridge-supplied `fields` object to the scalars the server is
 * willing to put in a log line. Unknown keys, non-scalar values (objects,
 * arrays, functions), non-finite numbers and anything past `DIAG_MAX_FIELDS`
 * are dropped silently — a malformed frame degrades to fewer fields, it never
 * throws and never blocks the log.
 */
export const sanitizeDiagFields = (
  fields: unknown,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  if (fields === null || typeof fields !== "object" || Array.isArray(fields)) {
    return out;
  }
  let kept = 0;
  for (const [key, value] of Object.entries(fields as Record<string, unknown>)) {
    if (kept >= DIAG_MAX_FIELDS) break;
    if (!DIAG_ALLOWED_FIELDS.has(key)) continue;
    if (typeof value === "string") {
      out[key] = value.slice(0, DIAG_MAX_VALUE_CHARS);
    } else if (typeof value === "number") {
      if (!Number.isFinite(value)) continue;
      out[key] = value;
    } else if (typeof value === "boolean") {
      out[key] = value;
    } else {
      continue; // objects/arrays/null/undefined — never widen a log line
    }
    kept += 1;
  }
  return out;
};

/**
 * Sandbox lifecycle phases the phase-4 orchestrator surfaces. Stubbed
 * here so phases 2-4 can land their imports without churning the shared
 * package again; the orchestrator populates the transitions when it
 * lands.
 */
export type SandboxPhase =
  | "idle"
  | "triggering"
  | "waiting-for-runner"
  | "booting"
  | "ready"
  | "degraded"
  | "suspended"
  | "terminated";
